import { z } from 'zod'
import { journalStates } from '@/features/sms/domain/smsJournal'

/**
 * Envoi d'une proposition par SMS et suivi des canaux (mandat `.input/proposition/`, tâches 4, 6 et 10). Règles :
 * docs/commercial/proposition-publique.md. Le serveur décide de tout (consentement administratif, numéro confirmé,
 * ligne fixe, modèle actif) : l'écran ne fait qu'afficher la raison.
 */

export const phoneLookupSchema = z.object({
  e164: z.string(),
  status: z.enum(['NONE', 'PENDING', 'IN_PROGRESS', 'DONE', 'FAILED']),
  phoneType: z.enum(['mobile', 'landline', 'voip', 'unknown']).nullable(),
  carrier: z.string().nullable(),
  smsCapable: z.boolean().nullable(),
  lookedUpAt: z.string().nullable(),
})
export type PhoneLookup = z.infer<typeof phoneLookupSchema>

export const proposalSmsOptionsSchema = z.object({
  // Null avant le correctif 20261122010000 (brouillon) : lu comme « non admissible », jamais comme une erreur.
  eligible: z
    .boolean()
    .nullable()
    .transform((value) => value ?? false),
  phones: z.array(
    z.object({
      kind: z.enum(['MOBILE', 'PHONE']),
      e164: z.string(),
      lookup: phoneLookupSchema,
      reason: z.string().nullable(),
    }),
  ),
})
export type ProposalSmsOptions = z.infer<typeof proposalSmsOptionsSchema>

export const proposalSmsResultSchema = z.object({
  state: z.string(),
  reason: z.string().nullable().optional(),
  replayed: z.boolean().optional(),
})
export type ProposalSmsResult = z.infer<typeof proposalSmsResultSchema>

export const proposalChannelsSchema = z.object({
  email: z.object({ status: z.string(), sentAt: z.string().nullable(), requestedAt: z.string() }).nullable(),
  sms: z
    .object({
      state: z.enum(journalStates),
      reason: z.string().nullable(),
      requestedAt: z.string(),
      e164: z.string(),
    })
    .nullable(),
  shortLink: z
    .object({
      clickCount: z.number(),
      firstClickedAt: z.string().nullable(),
      lastClickedAt: z.string().nullable(),
      expiresAt: z.string(),
      revokedAt: z.string().nullable(),
    })
    .nullable(),
  firstViewedAt: z.string().nullable(),
  lastViewedAt: z.string().nullable(),
  viewCount: z.number(),
})
export type ProposalChannels = z.infer<typeof proposalChannelsSchema>

export const clientPhoneLookupsSchema = z.object({
  phone: phoneLookupSchema.nullable(),
  mobilePhone: phoneLookupSchema.nullable(),
})
export type ClientPhoneLookups = z.infer<typeof clientPhoneLookupsSchema>

/** Libellé court de la capacité SMS d'un numéro (Telnyx Number Lookup, mis en cache). */
export function phoneLookupLabel(lookup: PhoneLookup): {
  label: string
  tone: 'success' | 'neutral' | 'warning'
} {
  if (lookup.status === 'NONE') return { label: 'Type de ligne non vérifié', tone: 'neutral' }
  if (lookup.status === 'PENDING' || lookup.status === 'IN_PROGRESS') {
    return { label: 'Vérification en cours', tone: 'neutral' }
  }
  if (lookup.status === 'FAILED') return { label: 'Vérification impossible', tone: 'warning' }
  if (lookup.smsCapable === true) return { label: 'Mobile : SMS disponible', tone: 'success' }
  if (lookup.smsCapable === false) return { label: 'Ligne fixe : pas de SMS', tone: 'warning' }
  if (lookup.phoneType === 'voip') return { label: 'VoIP : SMS à confirmer', tone: 'neutral' }
  return { label: 'Type inconnu : SMS à confirmer', tone: 'neutral' }
}

/** `+14505551234` → `450-555-1234`. */
export function displayE164(e164: string): string {
  const digits = e164.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
  return digits.length === 10 ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : e164
}
